// @spec GHG-STR-007
// @spec GHG-STR-008
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AIMappingsTab,
  CustomActivitiesList,
  CustomActivityDetailModal,
  CustomActivityForm,
  EmissionFactorDetailModal,
  EmissionFactorSearch,
  FactorCollectionsTab,
  FactorLibraryTab,
} from '@/features/ghg/components';
import type { CustomActivity } from '@/features/ghg/types';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

const factorLibrarySearchSchema = z.object({
  tab: z
    .enum(['library', 'ai-mappings', 'custom', 'collections', 'review'])
    .default('library')
    .catch('library'),
});

export const Route = createFileRoute('/_authenticated/ghg/emission-factors')({
  validateSearch: factorLibrarySearchSchema,
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.emissionFactors'),
  }),
  component: EmissionFactorsPage,
});

/**
 * Factor Library page — unified emission factor management.
 *
 * Four tabs:
 * 1. Factor Library — predefined + Climatiq factors used by the organization
 * 2. AI Mappings — learned activity→factor mappings from previous assignments
 * 3. Custom Factors — organization-specific custom emission factors
 * 4. Factor Search — full Climatiq search with filters
 */
function EmissionFactorsPage() {
  const { t } = useTranslation('ghg');
  const navigate = useNavigate();
  const { tab } = Route.useSearch();

  const [selectedClimatiqFactorId, setSelectedClimatiqFactorId] = useState<string | null>(null);
  const [selectedCustomActivityId, setSelectedCustomActivityId] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState<CustomActivity | null>(null);

  const [showFactorSearch, setShowFactorSearch] = useState(false);

  const handleTabChange = (value: string) => {
    if (value === 'review') {
      void navigate({ to: '/ghg/activity-data/assignment-queue' });
      return;
    }
    void navigate({
      to: '/ghg/emission-factors',
      search: { tab: value as 'library' | 'ai-mappings' | 'custom' | 'collections' },
      replace: true,
    });
  };

  const handleFormSuccess = () => {
    setIsCreateDialogOpen(false);
    setEditingActivity(null);
  };

  return (
    <div className="p-8">
      <div className="mx-auto max-w-6xl space-y-8">
        <header className="space-y-4">
          <h1 className="text-3xl font-semibold text-foreground">
            {t('factorLibraryPage.heading')}
          </h1>
          <p className="mt-1 text-muted-foreground">{t('factorLibraryPage.description')}</p>
        </header>

        <Tabs value={tab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList>
            <TabsTrigger value="library">{t('factorLibraryPage.tabLibrary')}</TabsTrigger>
            <TabsTrigger value="ai-mappings">{t('factorLibraryPage.tabAIMappings')}</TabsTrigger>
            <TabsTrigger value="custom">{t('factorLibraryPage.tabCustomFactors')}</TabsTrigger>
            <TabsTrigger value="collections">{t('factorLibraryPage.tabCollections')}</TabsTrigger>
            <TabsTrigger value="review">{t('factorLibraryPage.tabFactorReview')}</TabsTrigger>
          </TabsList>

          <TabsContent value="library" className="space-y-6">
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setShowFactorSearch(true); }}
              >
                <Search className="mr-2 h-4 w-4" />
                {t('factorLibraryPage.tabFactorSearch')}
              </Button>
            </div>
            <FactorLibraryTab />
          </TabsContent>

          <TabsContent value="ai-mappings" className="space-y-6">
            <AIMappingsTab />
          </TabsContent>

          <TabsContent value="custom" className="space-y-6">
            <CustomActivitiesList
              onView={(activity) => {
                setSelectedCustomActivityId(activity.id);
              }}
              onEdit={setEditingActivity}
              onCreate={() => {
                setIsCreateDialogOpen(true);
              }}
            />
          </TabsContent>

          <TabsContent value="collections" className="space-y-6">
            <FactorCollectionsTab />
          </TabsContent>

        {/* Factor Search is now a dialog triggered from Factor Library tab */}
        </Tabs>

        <EmissionFactorDetailModal
          factorId={selectedClimatiqFactorId}
          open={!!selectedClimatiqFactorId}
          onOpenChange={(open) => {
            if (!open) setSelectedClimatiqFactorId(null);
          }}
        />

        <CustomActivityDetailModal
          activityId={selectedCustomActivityId}
          open={selectedCustomActivityId != null}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedCustomActivityId(null);
            }
          }}
        />

        <Dialog
          open={isCreateDialogOpen || !!editingActivity}
          onOpenChange={(open) => {
            if (!open) {
              setIsCreateDialogOpen(false);
              setEditingActivity(null);
            }
          }}
        >
          <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingActivity
                  ? t('emissionFactorsPage.editCustomActivity')
                  : t('emissionFactorsPage.createCustomActivity')}
              </DialogTitle>
              <DialogDescription className="sr-only">
                {editingActivity
                  ? t('emissionFactorsPage.editCustomActivityDesc')
                  : t('emissionFactorsPage.createCustomActivityDesc')}
              </DialogDescription>
            </DialogHeader>
            <CustomActivityForm
              {...(editingActivity ? { activity: editingActivity } : {})}
              onSuccess={handleFormSuccess}
              onCancel={() => {
                setIsCreateDialogOpen(false);
                setEditingActivity(null);
              }}
            />
          </DialogContent>
        </Dialog>

        <Dialog open={showFactorSearch} onOpenChange={setShowFactorSearch}>
          <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('factorLibraryPage.tabFactorSearch')}</DialogTitle>
              <DialogDescription>
                Search the full Climatiq emission factor database.
              </DialogDescription>
            </DialogHeader>
            <EmissionFactorSearch
              onSelectFactor={(factorId, factor) => {
                if (factor?.selectorSource === 'custom') {
                  setSelectedClimatiqFactorId(null);
                  const customActivityId = factor.activityId.startsWith('custom:')
                    ? factor.activityId.slice('custom:'.length)
                    : null;
                  setSelectedCustomActivityId(customActivityId);
                  setShowFactorSearch(false);
                  return;
                }
                setSelectedCustomActivityId(null);
                setSelectedClimatiqFactorId(factorId);
                setShowFactorSearch(false);
              }}
            />
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
